import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import { useLang, BOOKING_URL } from "../i18n";
import { Reveal, Overline, EASE } from "./Reveal";

const FLAGS = {
  Italia: "🇮🇹", Italy: "🇮🇹",
  Svizzera: "🇨🇭", Switzerland: "🇨🇭",
};

const AUTOPLAY_MS = 6000;

const SLIDE_VARIANTS = {
  enter: (d) => ({ opacity: 0, x: d * 60 }),
  center: { opacity: 1, x: 0 },
  exit: (d) => ({ opacity: 0, x: d * -60 }),
};
const SLIDE_TRANSITION = { duration: 0.55, ease: EASE };
const DRAG_CONSTRAINTS = { left: 0, right: 0 };

const Reviews = () => {
  const { t } = useLang();
  const quotes = t.reviews.quotes;
  const [[index, dir], setIndex] = useState([0, 1]);
  const paused = useRef(false);

  const go = useCallback(
    (d) => setIndex(([i]) => [(i + d + quotes.length) % quotes.length, d]),
    [quotes.length]
  );

  useEffect(() => {
    const id = setInterval(() => {
      if (!paused.current) go(1);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [go]);

  const q = quotes[index];

  return (
    <section id="recensioni" data-testid="reviews-section" className="py-28 lg:py-40 bg-[#0F1A33] text-[#F9F8F6]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <Overline light>{t.reviews.overline}</Overline>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-4">{t.reviews.title}</h2>
              <div className="mt-10 flex items-end gap-4">
                <span className="font-serif text-8xl leading-none text-[#E8B54A]">9.4</span>
                <div className="pb-2">
                  <p className="font-serif italic text-2xl">{t.reviews.scoreLabel}</p>
                  <p className="text-xs text-[#F9F8F6]/50 mt-1">{t.reviews.basedOn}</p>
                </div>
              </div>
              <div className="mt-10 space-y-4">
                {t.reviews.categories.map((c, i) => (
                  <div key={c.label} data-testid={`review-score-${i}`}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-[#F9F8F6]/70">{c.label}</span>
                      <span className="text-[#E8B54A]">{c.value}</span>
                    </div>
                    <div className="h-[3px] bg-[#F9F8F6]/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${c.value * 10}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: i * 0.08, ease: EASE }}
                        className="h-full bg-[#E8B54A] rounded-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <a
                data-testid="reviews-booking-link"
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-10 inline-flex items-center gap-2 group text-sm tracking-wide text-[#F9F8F6]"
              >
                <span className="link-underline">{t.reviews.readAll}</span>
                <ArrowUpRight size={16} className="text-[#E8B54A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </a>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1} className="lg:col-span-8">
          <div
            data-testid="reviews-carousel"
            className="relative flex flex-col justify-between min-h-[420px] rounded-[1.5rem] border border-[#F9F8F6]/10 bg-[#F9F8F6]/[0.03] p-8 md:p-12 overflow-hidden"
            onMouseEnter={() => (paused.current = true)}
            onMouseLeave={() => (paused.current = false)}
          >
            <Quote size={26} className="text-[#E8B54A] shrink-0" />

            <div className="relative flex-1 mt-6">
              <AnimatePresence mode="wait" custom={dir}>
                <motion.div
                  key={index}
                  custom={dir}
                  variants={SLIDE_VARIANTS}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={SLIDE_TRANSITION}
                  drag="x"
                  dragConstraints={DRAG_CONSTRAINTS}
                  dragElastic={0.15}
                  onDragEnd={(_, info) => {
                    if (info.offset.x < -70) go(1);
                    else if (info.offset.x > 70) go(-1);
                  }}
                  className="cursor-grab active:cursor-grabbing"
                >
                  <blockquote
                    data-testid={`carousel-review-text-${index}`}
                    className="font-serif italic text-2xl md:text-[2rem] leading-snug text-[#F9F8F6]/90 max-w-2xl"
                  >
                    “{q.text}”
                  </blockquote>
                  <div className="mt-10 flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-[#2852A6] flex items-center justify-center font-serif text-xl text-[#F9F8F6] select-none">
                      {q.name.charAt(0)}
                    </div>
                    <div>
                      <p data-testid="carousel-review-author" className="text-sm tracking-[0.15em] uppercase">
                        {q.name}
                        <span
                          data-testid="carousel-review-score"
                          className="ml-3 inline-flex items-center rounded-md bg-[#E8B54A] text-[#0F1A33] px-2 py-0.5 text-[11px] font-semibold tracking-normal align-middle"
                        >
                          {q.score}
                        </span>
                      </p>
                      <p className="text-xs text-[#F9F8F6]/50 mt-0.5">
                        <span className="mr-1.5 text-sm align-middle">{FLAGS[q.country] || ""}</span>
                        {q.country} · {q.room} · {t.reviews.verified}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-10 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                {quotes.map((quote, i) => (
                  <button
                    key={quote.text}
                    data-testid={`carousel-dot-${i}`}
                    aria-label={`Review ${i + 1}`}
                    onClick={() => setIndex([i, i > index ? 1 : -1])}
                    className={`h-1.5 rounded-full transition-all duration-400 ${
                      i === index ? "w-8 bg-[#E8B54A]" : "w-1.5 bg-[#F9F8F6]/25 hover:bg-[#F9F8F6]/50"
                    }`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid="carousel-prev"
                  aria-label="Previous review"
                  onClick={() => go(-1)}
                  className="h-11 w-11 rounded-full border border-[#F9F8F6]/20 flex items-center justify-center hover:bg-[#F9F8F6] hover:text-[#0F1A33] transition-colors duration-300"
                >
                  <ArrowLeft size={17} />
                </button>
                <button
                  data-testid="carousel-next"
                  aria-label="Next review"
                  onClick={() => go(1)}
                  className="h-11 w-11 rounded-full border border-[#F9F8F6]/20 flex items-center justify-center hover:bg-[#F9F8F6] hover:text-[#0F1A33] transition-colors duration-300"
                >
                  <ArrowRight size={17} />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Reviews;
